/**
 * @file This custom hook is responsible for fetching asset metadata, including categories, statuses, and custom properties.
 * It handles the asynchronous data fetching, loading states, and error handling, providing a clean interface for components
 * that need access to this information.
 */
import { useState, useEffect } from 'react';
import { FetchCustomProperties } from "../api/assetCustomPropertiesService";
import { FetchCategories } from "../api/assetCategoriesService";
import { FetchStatuses } from "../api/assetStatusesService";
import {FetchLocations} from "../api/locationService";

/**
 * Fetches asset metadata, including categories, statuses, and custom properties for a given project.
 * This hook abstracts the data-fetching logic, loading states, and error handling away from the components.
 *
 * @param {string} selectedProjectId - The ID of the project for which to fetch asset metadata.
 * @param {string} selectedCategoryId - The ID of the currently selected category, used to fetch related custom properties.
 * @param {Function} getAccessToken - Function to get the access token for API calls.
 * @param {*} reloadFlag - Value toggled to trigger a metadata reload (optional).
 * @returns {{categories: Array, statuses: Array, customProperties: Array, isLoading: boolean, error: Error|null}}
 * An object containing the fetched metadata, loading state, and any potential errors.
 */
export const useAssetMetadata = (selectedProjectId, selectedCategoryId, getAccessToken, reloadFlag) => {
    // console.log("useAssetMetadata hook initialized with:", { selectedProjectId });
    
    const [categories, setCategories] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [customProperties, setCustomProperties] = useState([]);
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * The primary effect hook that triggers the metadata fetch whenever the selected project ID or reloadFlag changes.
     * It ensures that the metadata is always in sync with the currently selected project and can be refreshed on demand.
     */
    useEffect(() => {
        console.log("useAssetMetadata hook called with selectedProjectId:", selectedProjectId, "reloadFlag:", reloadFlag);
        if (!selectedProjectId || selectedProjectId.trim() === '') {
            console.log("No projectId available yet for metadata fetch");
            return;
        }
        const fetchMetaData = async () => {
            try {
                console.log("Fetching asset metadata for project:", selectedProjectId);
                
                if (!getAccessToken) {
                    console.error("getAccessToken is not available in useAssetMetadata");
                    setError("Authentication not available");
                    setIsLoading(false);
                    return;
                }
                
                const accessToken = await getAccessToken();
                const params = {
                    selectedProjectId: selectedProjectId,
                    selectedCategoryId: selectedCategoryId,
                    accessToken: accessToken
                };
                setIsLoading(true);
                setError(null);

                /**
                 * Fetches all required metadata in parallel to improve performance.
                 * This includes custom properties, categories, and statuses.
                 */
                const [customPropsResponse, categoriesResponse, statusesResponse, locationsResponse] = await Promise.all([
                    FetchCustomProperties(params),
                    FetchCategories(params),
                    FetchStatuses(params),
                    FetchLocations(params)
                ]);

                // Store raw arrays for the component to process
                const categoriesData = categoriesResponse.results || [];
                const statusesData = statusesResponse.results || [];
                const customPropsData = customPropsResponse.results || [];
                const locationsData = locationsResponse.results || [];

                // console.log('Raw API responses:');
                // console.log('categoriesData:', categoriesData);
                // console.log('statusesData:', statusesData);
                // console.log('customPropsData:', customPropsData);
                // console.log('locationsData:', locationsData);

                setCategories(categoriesData);
                setStatuses(statusesData);
                setCustomProperties(customPropsData);
                setLocations(locationsData);

                // console.log("Asset metadata fetched successfully:", {
                //     categories: categoriesData.length,
                //     statuses: statusesData.length,
                //     customProps: customPropsData.length,
                //     locations: locationsData.length
                // });

                setIsLoading(false);

            } catch (err) {
                console.error('Error fetching asset metadata:', err);
                setError(err.message);
                setIsLoading(false);
            }
        };

        fetchMetaData();
    }, [selectedProjectId, reloadFlag]); // Depend on both selectedProjectId and reloadFlag

    return {
        categories,
        statuses,
        customProperties,
        locations,
        isLoading,
        error
    };
};